import { Component, OnInit, signal, inject, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { SubResponsibleRegistryService } from '../../../services/sub-responsible-registry.service';
import { SubResponsible } from '../../../models/sub-responsible.model';
import { MessageService, MenuItem, ConfirmationService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationDialogService } from '../../../../core/services/confirmation-dialog.service';
import { ContractsRegistryComponent } from '../../contracts-registry/contracts-registry.component';
import { NoSpecialCharsValidator } from '../../../../core/validators/no-special-chars.validator';
import { CityCountryFormatValidator } from '../../../../core/validators/city-country-format.validator';
import { ValidationErrorComponent } from '../../../../core/shared/validation-error/validation-error.component';
import { FormDropdownItem, FormDropdownService } from '../../../../core/services/form-dropdown.service';

// PrimeNG Imports
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-sub-responsible-detail',
  standalone: true,
  providers: [MessageService],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    TagModule,
    ProgressSpinnerModule,
    InputTextModule,
    SelectModule,
    ToastModule,
    BreadcrumbModule,
    ConfirmDialogModule,
    ContractsRegistryComponent,
    ValidationErrorComponent,
  ],
  templateUrl: './sub-responsible-detail.component.html',
  styleUrls: ['./sub-responsible-detail.component.scss'],
})
export class SubResponsibleDetailModalComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private titleService = inject(Title);
  private subResponsibleService = inject(SubResponsibleRegistryService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private confirmDialogService = inject(ConfirmationDialogService);
  private formDropdownService = inject(FormDropdownService);
  private fb = inject(FormBuilder);

  isEditMode = signal(false);
  isLoading = signal(true);
  breadcrumbItems = signal<MenuItem[]>([]);
  subResponsibleId = signal<string | undefined>(undefined);
  
  // FormGroup reattivo
  subResponsibleForm: FormGroup = this.fb.group({
    businessName: ['', [Validators.required, NoSpecialCharsValidator('free-text')]],
    companyType: ['', [Validators.required]],
    headquarters: ['', [Validators.required, NoSpecialCharsValidator('address'), CityCountryFormatValidator()]],
    email: ['', [Validators.email, NoSpecialCharsValidator('strict')]],
  });

  // Initialize with a complete default object structure
  manager = signal<SubResponsible>({
    businessName: '',
    companyType: '',
    headquarters: '',
    email: '',
  });
  companyTypeOptions: FormDropdownItem[] = [];
  
  // Effect per gestire enable/disable dei campi
  constructor() {
    effect(() => {
      const isEdit = this.isEditMode();
      if (isEdit) {
        this.enableFields();
      } else {
        this.disableFields();
      }
    });
  }

  ngOnInit(): void {
    this.titleService.setTitle('Dettaglio - PalEasy');
    this.loadCompanyTypes();

    // Inizializza breadcrumb con valore di default
    this.breadcrumbItems.set([
      { label: 'Registro Sub-Responsabili', routerLink: '/axa/sub-responsibles-registry' },
      { label: 'Dettaglio Sub-Responsabile' }
    ]);

    // Get manager ID from route params
    this.route.params.subscribe(params => {
      const managerId = params['id'];
      if (managerId) {
        this.subResponsibleId.set(managerId);
        this.loadManagerDetails(managerId);
      } else {
        console.error('No sub-responsible ID provided');
        this.isLoading.set(false);
      }
    });
  }

  private loadCompanyTypes(): void {
    this.formDropdownService.getFormDropdownValues('CREATE_SUPPLIER', 'SUPPLIER_TYPE').subscribe({
      next: (response) => {
        if (response.status && response.data) {
          this.companyTypeOptions = response.data;
        }
      },
      error: (err) => {
        console.error('Error loading company types:', err);
      },
    });
  }

  private loadManagerDetails(id: string): void {
    this.isLoading.set(true);
    this.subResponsibleService.getSubResponsible(id).subscribe({
      next: (response: any) => {
        if (response?.status && response?.data) {
          const apiData = response.data;
          
          // Map API response to component model
          const mappedSubResponsible: SubResponsible = {
            subResponsibleId: apiData.subResponsibleId || apiData.id,
            businessName: apiData.businessName || '',
            companyType: apiData.companyType || '',
            headquarters: apiData.headquarters || '',
            email: apiData.email || '',
          };
          
          this.manager.set(mappedSubResponsible);
          
          // Popola il form reattivo
          this.subResponsibleForm.patchValue({
            businessName: mappedSubResponsible.businessName,
            companyType: mappedSubResponsible.companyType,
            headquarters: mappedSubResponsible.headquarters,
            email: mappedSubResponsible.email,
          });
          
          // Aggiorna breadcrumb con il nome del sub-responsabile
          this.breadcrumbItems.set([
            { label: 'Registro Sub-Responsabili', routerLink: '/axa/sub-responsibles-registry' },
            { label: mappedSubResponsible.businessName || 'Dettaglio Sub-Responsabile' }
          ]);
          
          this.isLoading.set(false);
        } else {
          console.error('Invalid API response format:', response);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Formato dati non valido ricevuto dal server',
            life: 5000
          });
          this.isLoading.set(false);
        }
      },
      error: (error) => {
        console.error('Error loading sub-responsible details:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore nel recupero dei dati',
          detail: 'Impossibile caricare i dettagli del sub-responsabile. Riprova più tardi.',
          life: 5000
        });
        this.isLoading.set(false);
      }
    });
  }

  private enableFields(): void {
    Object.keys(this.subResponsibleForm.controls).forEach(key => {
      this.subResponsibleForm.get(key)?.enable({ emitEvent: false });
    });
  }

  private disableFields(): void {
    Object.keys(this.subResponsibleForm.controls).forEach(key => {
      this.subResponsibleForm.get(key)?.disable({ emitEvent: false });
    });
  }

  toggleEdit(): void {
    this.isEditMode.set(true);
  }

  cancelEdit(): void {
    // Mostra dialog di conferma prima di annullare
    this.confirmDialogService.confirmCancelEdit(() => {
      this.isEditMode.set(false);
      const id = this.manager().subResponsibleId;
      if (id) {
        this.loadManagerDetails(id);
      }
    });
  }

  /**
   * Controlla se ci sono campi con caratteri speciali non permessi
   * @returns Array di nomi campi con errori
   */
  private hasSpecialCharsErrors(): string[] {
    const fieldsWithErrors: string[] = [];
    const fieldLabels: { [key: string]: string } = {
      businessName: 'Ragione Sociale',
      companyType: 'Tipo societa',
      headquarters: 'Sede',
      email: 'Email',
    };

    Object.keys(this.subResponsibleForm.controls).forEach(key => {
      const control = this.subResponsibleForm.get(key);
      if (control?.hasError('specialChars') && fieldLabels[key]) {
        fieldsWithErrors.push(fieldLabels[key]);
      }
    });

    return fieldsWithErrors;
  }

  save(): void {
    // ✅ XSS VALIDATION: Controlla caratteri speciali PRIMA della validazione required
    const specialCharsFields = this.hasSpecialCharsErrors();
    if (specialCharsFields.length > 0) {
      Object.keys(this.subResponsibleForm.controls).forEach(key => {
        this.subResponsibleForm.get(key)?.markAsTouched();
      });
      this.messageService.add({
        severity: 'error',
        summary: 'Caratteri non permessi',
        detail: `I seguenti campi contengono caratteri speciali non permessi (< > " ' & / \\): ${specialCharsFields.join(', ')}. Correggi i campi evidenziati.`,
        life: 6000,
      });
      return;
    }

    // Valida il form prima del salvataggio
    if (this.subResponsibleForm.invalid) {
      Object.keys(this.subResponsibleForm.controls).forEach(key => {
        this.subResponsibleForm.get(key)?.markAsTouched();
      });
      this.messageService.add({
        severity: 'warn',
        summary: 'Validazione',
        detail: 'Correggi gli errori nei campi prima di salvare',
        life: 3000
      });
      return;
    }
    
    const currentManager = this.manager();
    
    if (!currentManager.subResponsibleId) {
      console.error('Cannot save: sub-responsible ID is missing');
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'ID sub-responsabile mancante. Impossibile salvare.',
        life: 3000
      });
      return;
    }

    // Prepara payload dal form
    const formValues = this.subResponsibleForm.value;
    const payload: SubResponsible = {
      subResponsibleId: currentManager.subResponsibleId,
      businessName: formValues.businessName,
      companyType: formValues.companyType,
      headquarters: formValues.headquarters,
      email: formValues.email,
    };

    // Call PUT API to update sub-responsible
    this.subResponsibleService.updateSubResponsiblePut(currentManager.subResponsibleId, payload).subscribe({
      next: (response: any) => {
        console.log('Sub-Responsible updated successfully:', response);
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Sub-Responsabile aggiornato con successo',
          life: 3000
        });
        this.isEditMode.set(false);
        // Ricarica i dati per aggiornare la vista
        if (currentManager.subResponsibleId) {
          this.loadManagerDetails(currentManager.subResponsibleId);
        }
      },
      error: (error: any) => {
        console.error('Error updating sub-responsible:', error);
        const errorMessage = error?.error?.message || 'Impossibile salvare le modifiche. Riprova più tardi.';
        this.messageService.add({
          severity: 'error',
          summary: 'Errore nel salvataggio',
          detail: errorMessage,
          life: 5000
        });
      }
    });
  }

  getStatusDisplay(status: string): string {
    switch (status) {
      case 'ACTIVE':
        return 'ATTIVO';
      case 'INACTIVE':
        return 'CESSATO';
      case 'DRAFT':
        return 'INCOMPLETO';
      default:
        return status;
    }
  }

  getStatusSeverity(status: string): 'success' | 'danger' | 'secondary' {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'danger';
      case 'DRAFT':
        return 'secondary';
      default:
        return 'secondary';
    }
  }

  goBack(): void {
    if (this.isEditMode()) {
      // Se in modalità edit, chiedi conferma prima di uscire
      this.confirmDialogService.confirmCancelEdit(() => {
        this.router.navigate(['/axa/sub-responsibles-registry']);
      });
    } else {
      // Se non in modalità edit, naviga direttamente
      this.router.navigate(['/axa/sub-responsibles-registry']);
    }
  }
}
